import {Component, Inject} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {AuthService} from "../../../core/services/auth.service";
import {NotificationService} from "../../../core/services/notification.service";
import {PushProgressService} from "../../../core/services/push-progress.service";
import {ConfirmationDialogComponent} from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";
import {IPushWorkoutsToLocationsRequest} from "../../../core/api/controllers/push-jobs/dto/push-job";

export interface IPushWorkoutsToLocationDialogData {
  workoutIds: string[];
}

@Component({
  selector: 'app-push-workouts-to-location-dialog',
  templateUrl: './push-workouts-to-location-dialog.component.html',
  styleUrls: ['./push-workouts-to-location-dialog.component.scss']
})
export class PushWorkoutsToLocationDialogComponent {
  isLoading = false;
  /** Uncheck to push silently, e.g. when correcting a mistake. */
  notifyUsers = true;
  readonly sourceLocationId = AuthService.getSelectedLocationId();

  constructor(
      @Inject(MAT_DIALOG_DATA) private data: IPushWorkoutsToLocationDialogData,
      private dialogRef: MatDialogRef<PushWorkoutsToLocationDialogComponent>,
      private messageService: NotificationService,
      private progress: PushProgressService,
      private dialog: MatDialog
  ) {}

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(locationIds: string[]): void {
    this.start(this.createPayload(locationIds, false));
  }

  pushToAll(): void {
    const count = this.workoutCount;
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: "Push to all active locations?",
        message: `Push ${count} selected workout${count === 1 ? "" : "s"} to every active, non-deleted location except the current source location?`,
        confirmButtonLabel: "Start push",
      },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.start(this.createPayload([], true));
    });
  }

  private start(payload: IPushWorkoutsToLocationsRequest): void {
    this.isLoading = true;
    this.progress.create("Workout", payload).subscribe({
      next: (job) => {
        this.dialogRef.close(job);
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.handleStartError(error);
      }
    });
  }

  get workoutCount(): number {
    return this.data.workoutIds?.length ?? 0;
  }

  get confirmationPrefix(): string {
    return `Push ${this.workoutCount} selected workout${this.workoutCount === 1 ? "" : "s"}`;
  }

  createPayload(locationIds: string[], toAllLocations: boolean): IPushWorkoutsToLocationsRequest {
    return {
      workoutIds: this.data.workoutIds,
      sourceLocationId: AuthService.getSelectedLocationId(),
      destinationLocationIds: locationIds,
      toAllLocations,
      notifyUsers: this.notifyUsers
    };
  }

  private handleStartError(error: any): void {
    if (error?.status === 409) {
      this.messageService.warn("Another workout push is already running.");
      this.progress.openActiveJobIfOwned("Workout");
      return;
    }
    this.messageService.error(error);
  }
}
